import { loadFont } from "@remotion/google-fonts/Poppins";
import { AbsoluteFill, Img, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";

// Only the weights these compositions actually use, and latin only. The unqualified call pulls
// every weight and subset — 54 network requests during a render that must not depend on the
// network being kind.
const { fontFamily } = loadFont("normal", { weights: ["500", "600", "700"], subsets: ["latin"] });

// Instagram draws its own chrome over a reel — roughly the top 120px and the bottom 320px of a
// 1920-tall frame (caption, audio strip, action buttons). Anything placed there is covered on a
// real phone even though it looks fine in the preview, so the whole composition lives inside this
// band rather than using the full height.
const SAFE_TOP = 210;
const SAFE_BOTTOM = 400;

export type BookendReelProps = {
	/** Gateway city that gets visited twice. */
	city: string;
	/** Share of trips that stop there twice, as a whole number. */
	pct: number;
	/** Destination that is visited once, for contrast. */
	contrastCity: string;
	contrastPct: number;
	contrastNights: number;
	/** Photo of the gateway city — shown at the start AND the end, which is the whole point. */
	cityPhoto: string;
	/** Somewhere in the middle of the trip. */
	midPhoto: string;
};

/**
 * The bookend reel: a gateway city is entered twice, so the video returns to the identical opening
 * photograph before the statistic appears. The picture makes the argument, the number confirms it.
 *
 * Timed for the 8s / 240-frame slot at 30fps. The closing frame is composed to match the opening
 * one so the loop has no visible seam — reels replay immediately, and a hard cut reads as a mistake.
 */
export const BookendReel: React.FC<BookendReelProps> = ({
	city,
	pct,
	contrastCity,
	contrastPct,
	contrastNights,
	cityPhoto,
	midPhoto,
}) => {
	const frame = useCurrentFrame();
	const { fps, width, height } = useVideoConfig();

	// One continuous push-in across the whole reel. Restarting the zoom on each photo change would
	// draw attention to the cut; holding it steady lets the return to the first photo register as a
	// return rather than a new shot.
	const zoom = interpolate(frame, [0, 240], [1, 1.14], { extrapolateRight: "clamp" });

	// Away from the city between 2.4s and 4.0s, then back — the return is the argument.
	const away = interpolate(frame, [66, 78, 114, 126], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

	const headline = spring({ frame: frame - 4, fps, config: { damping: 200 } });
	const routeDraw = interpolate(frame, [24, 120], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
	const counter = interpolate(frame, [120, 158], [0, pct], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
	const statIn = spring({ frame: frame - 118, fps, config: { damping: 200 } });
	const subIn = interpolate(frame, [150, 168], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
	const contrastIn = interpolate(frame, [180, 196], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
	// Everything below the headline clears before the loop point, so frame 239 ≈ frame 0.
	const clearOut = interpolate(frame, [216, 236], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

	const pin = (at: number) => spring({ frame: frame - at, fps, config: { damping: 14, stiffness: 200 } });

	return (
		<AbsoluteFill style={{ backgroundColor: "#092e37", fontFamily }}>
			<AbsoluteFill style={{ transform: `scale(${zoom})` }}>
				<Img src={cityPhoto} style={{ width, height, objectFit: "cover" }} />
			</AbsoluteFill>
			<AbsoluteFill style={{ transform: `scale(${zoom})`, opacity: away }}>
				<Img src={midPhoto} style={{ width, height, objectFit: "cover" }} />
			</AbsoluteFill>

			<AbsoluteFill
				style={{
					background:
						"linear-gradient(180deg, rgba(9,46,55,.74) 0%, rgba(9,46,55,.34) 32%, rgba(9,46,55,.88) 72%, rgba(9,46,55,.97) 100%)",
				}}
			/>

			<AbsoluteFill style={{ padding: `${SAFE_TOP}px 90px ${SAFE_BOTTOM}px`, display: "flex", flexDirection: "column" }}>
				<div style={{ fontSize: 34, fontWeight: 600, letterSpacing: 4, textTransform: "uppercase", color: "#4fe3b4" }}>Stippl data</div>

				<div
					style={{
						fontSize: 122,
						lineHeight: 1.02,
						fontWeight: 700,
						color: "#fff",
						marginTop: 26,
						letterSpacing: -2,
						opacity: headline,
						transform: `translateY(${interpolate(headline, [0, 1], [40, 0])}px)`,
					}}
				>
					Nobody stays in {city} once.
				</div>

				<div style={{ marginTop: "auto", opacity: clearOut }}>
					<div style={{ display: "flex", alignItems: "center", gap: 18 }}>
						<Dot scale={pin(24)} />
						<Leg progress={interpolate(routeDraw, [0, 0.5], [0, 1], { extrapolateRight: "clamp" })} />
						<Dot scale={pin(72)} hollow />
						<Leg progress={interpolate(routeDraw, [0.5, 1], [0, 1], { extrapolateLeft: "clamp" })} />
						<Dot scale={pin(112)} />
					</div>
					<div style={{ display: "flex", justifyContent: "space-between", fontSize: 30, color: "rgba(255,255,255,.72)", marginTop: 18 }}>
						<span>{city}</span>
						<span>{city}</span>
					</div>

					<div
						style={{
							fontSize: 250,
							fontWeight: 700,
							lineHeight: 0.92,
							color: "#13c892",
							marginTop: 54,
							fontVariantNumeric: "tabular-nums",
							opacity: statIn,
							transform: `scale(${interpolate(statIn, [0, 1], [0.86, 1])})`,
						}}
					>
						{Math.round(counter)}%
					</div>

					<div style={{ fontSize: 46, color: "#fff", marginTop: 20, maxWidth: 780, opacity: subIn }}>
						of trips through {city} stop there twice: in, then out again.
					</div>

					<div style={{ fontSize: 38, color: "rgba(255,255,255,.78)", marginTop: 26, opacity: contrastIn }}>
						{contrastCity}? {contrastPct}%. One visit, {contrastNights} nights.
					</div>

				</div>
			</AbsoluteFill>
		</AbsoluteFill>
	);
};

const Dot: React.FC<{ scale: number; hollow?: boolean }> = ({ scale, hollow }) => (
	<div
		style={{
			width: 34,
			height: 34,
			borderRadius: "50%",
			flex: "none",
			transform: `scale(${scale})`,
			background: hollow ? "transparent" : "#13c892",
			border: hollow ? "5px solid rgba(255,255,255,.55)" : "none",
			boxShadow: hollow ? "none" : "0 0 0 12px rgba(19,200,146,.22)",
		}}
	/>
);

const Leg: React.FC<{ progress: number }> = ({ progress }) => (
	<div style={{ flex: 1, height: 6, background: "rgba(255,255,255,.24)", borderRadius: 3, overflow: "hidden" }}>
		<div style={{ width: `${progress * 100}%`, height: "100%", background: "#13c892" }} />
	</div>
);

export default BookendReel;
